import { useEffect, useState, type FormEvent } from "react";
import { postPublicCheckIn } from "../../api/client";
import { useEventContent } from "../../context/EventContentContext";
import { useRsvpReservation } from "../../hooks/useRsvpReservation";
import { useBotProtection } from "../../hooks/useBotProtection";
import { markOnlineCheckIn } from "../../lib/rsvpAck";
import { HoneypotField } from "./HoneypotField";
import { TurnstileWidget } from "./TurnstileWidget";

type State = "idle" | "loading" | "err";

export function CheckInForm({
  eventId,
  onSuccess,
}: {
  eventId?: number | null;
  onSuccess?: () => void;
}) {
  const content = useEventContent();
  const resolvedEventId = eventId ?? content.eventId ?? null;
  const reservation = useRsvpReservation(resolvedEventId);
  const alreadyIn = Boolean(reservation?.checkedInAt);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<string | null>(null);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  useEffect(() => {
    if (!reservation) return;
    setName(reservation.name);
    setEmail(reservation.email);
    setPhone(reservation.phone);
  }, [reservation]);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!ready) return;
    setState("loading");
    setError(null);
    try {
      await postPublicCheckIn({
        name,
        email,
        phone: phone || undefined,
        ...botFields,
      });
      markOnlineCheckIn({
        eventId: resolvedEventId,
        name,
        email,
        phone,
      });
      setState("idle");
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not check you in");
      setState("err");
      resetBotProtection();
    }
  }

  if (alreadyIn) {
    const who = reservation?.name.trim();
    return (
      <div className="form-success">
        <div className="sec-eyebrow"><em>checked in</em></div>
        <h3 className="sec-title sec-title-sm">
          {who ? `${who}, you're watching` : "You're checked in"}
        </h3>
        <p className="lede-serif">
          You’re checked in online for this overnight. Welcome.
        </p>
      </div>
    );
  }

  return (
    <form className="form-stack" onSubmit={onSubmit}>
      <HoneypotField value={website} onChange={setWebsite} />
      <p className="lede-serif">Enter your details to check in as an online guest.</p>
      <label>
        <span>Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} required disabled={state === "loading"} />
      </label>
      <label>
        <span>Email</span>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={state === "loading"} />
      </label>
      <label>
        <span>Phone (optional)</span>
        <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={state === "loading"} />
      </label>
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      <button type="submit" className="btn btn-primary" disabled={state === "loading" || !ready}>
        {state === "loading" ? "Checking in…" : "Check in"}
      </button>
      {error && <div className="form-error">{error}</div>}
    </form>
  );
}
